import React, { useState, useEffect, createContext } from "react";
import {
  Box,
  Typography,
  Divider,
  Stack,
  Paper,
  IconButton,
  TextField,
  AppBar,
  Toolbar,
  InputLabel,
  Drawer,
  useMediaQuery,
  useTheme,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Menu,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
// import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MenuIcon from "@mui/icons-material/Menu";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useClerk, useUser } from "@clerk/clerk-react";
import { ReactComponent as Delete } from "../static/Icons/trash.svg";
import Logo from "../static/Icons/mcp-logo.svg";
import { ReactComponent as Support } from "../static/Icons/support.svg";
import { ReactComponent as Account } from "../static/Icons/account.svg";
import { ReactComponent as Logout } from "../static/Icons/logout.svg";
import ButtonCommon from "../CommonComponents/ButtonCommon";
import CommonModal from "../CommonComponents/CommonModal";
import { showErrorToast, showSuccessToast } from "../toastUtils";
import { createServer, deleteServer, getAllServers } from "../api/server";
import { AccountCircleOutlined, KeyboardArrowDown } from "@mui/icons-material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import AppsIcon from "@mui/icons-material/Apps";

export const RightSideContainer = createContext();

const sidebarWidth = 250;

const DashboardLayout = () => {
  const [openModal, setOpenModal] = useState(false);
  const [newServerName, setNewServerName] = useState("");
  // const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // State to store the list of servers
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState(null);
  const [allServerLoading, setAllServerLoading] = useState(true);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedServerToDelete, setSelectedServerToDelete] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const { id: serverIdFromUrl } = useParams();

  const { signOut } = useClerk();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  // const params = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [divHeight, setDivHeight] = useState(0);
  const divRef = React.useRef(null);
  const isMainRoute = location.pathname === "/dashboard";
  const [anchorEl, setAnchorEl] = useState(null);
  const handleUserClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUpgrade = () => {
    // Navigate to upgrade page
    navigate("/dashboard/upgrade");
    setAnchorEl(null); // Close the menu
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const updateHeight = () => {
      if (divRef.current) {
        setDivHeight(divRef.current.clientHeight);
      }
    };

    updateHeight(); // Initial measure
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, [allServerLoading]);

  useEffect(() => {
    if (selectedServer) {
      localStorage.setItem("server_id", selectedServer.mcp_server_id);
    }
  }, [selectedServer]);

  const fetchAllServers = async () => {
    setAllServerLoading(true);
    try {
      const res = await getAllServers();
      if (res.success) {
        setServers(res.data);
        if (res.data.length > 0) {
          setSelectedServer(res.data[0]);
          if (!location.pathname.startsWith("/dashboard/oauth/callback")) {
            navigate(`server/${res.data[0].mcp_server_id}`);
          }
        } else {
          setSelectedServer(null);
        }
      } else {
        showErrorToast(res.message);
      }
    } catch (err) {
      showErrorToast(err);
    } finally {
      setAllServerLoading(false);
    }
  };

  useEffect(() => {
    fetchAllServers();
    // eslint-disable-next-line
  }, []);

  // Effect to find and set the selected server based on the URL parameter
  useEffect(() => {
    const serverId = serverIdFromUrl;
    if (serverId && servers.length > 0) {
      const server = servers.find(
        (server) => server.mcp_server_id === serverId
      );
      setSelectedServer(server || null);
    } else {
      setSelectedServer(null);
    }
  }, [serverIdFromUrl, servers]);

  useEffect(() => {
    if (user) {
      localStorage.setItem("mcp_user_id", user.id);
    }
  }, [user]);

  const handleServerClick = (server) => {
    setSelectedServer(server);
    navigate(`/dashboard/server/${server.mcp_server_id}`);
    if (isMobile) {
      setMobileOpen(false); // Close drawer after selection on mobile
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      handleClose();
      localStorage.clear();
      window.location.href = "/signin";
    } catch (error) {
      showErrorToast("error in signout", error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Function to delete a server
  const handleDeleteServer = async () => {
    setDeleteLoader(true);
    try {
      const res = await deleteServer(selectedServerToDelete);

      if (res.success) {
        const resData = await getAllServers();
        setServers(resData.data || []);

        if (selectedServer?.mcp_server_id === selectedServerToDelete) {
          const newSelected = resData?.data[0] || null;
          setSelectedServer(newSelected);
          if (newSelected) {
            navigate(`server/${newSelected.mcp_server_id}`);
          } else {
            navigate(`/dashboard`);
          }
        }
        showSuccessToast(res.message);
      } else {
        showErrorToast(res.message);
      }
    } catch (err) {
      showErrorToast(err.message);
    } finally {
      setOpenDeleteModal(false);
      setSelectedServerToDelete(null);
      // Only clear selectedServer if no fallback is selected in try block
      // (e.g., when there are no more servers left)
      if (
        selectedServer?.mcp_server_id === selectedServerToDelete &&
        (!selectedServer || !servers.length)
      ) {
        setSelectedServer(null);
      }
      setDeleteLoader(false);
    }
  };

  const handleCreateServer = async () => {
    if (!newServerName.trim()) {
      showErrorToast("Server name is required");
      return;
    }
    setLoading(true);
    try {
      const res = await createServer({ server_name: newServerName.trim() });
      if (res.success) {
        const createdServerId = res.data.server_id;
        const resData = await getAllServers();
        if (resData.success) {
          setServers(resData.data);
          const matchedServer = resData.data.find(
            (server) => server.mcp_server_id === createdServerId
          );
          if (matchedServer) {
            setSelectedServer(matchedServer);
            navigate(`server/${matchedServer.mcp_server_id}`);
          } else {
            // fallback to first server if not found (optional)
            setSelectedServer(resData.data[0] || null);
            if (resData.data.length > 0) {
              navigate(`server/${resData.data[0].mcp_server_id}`);
            }
          }
        }
        setNewServerName("");
        setOpenModal(false);
        showSuccessToast(res.message);
      } else {
        showErrorToast(res.message);
      }
    } catch (err) {
      showErrorToast(err);
    } finally {
      setLoading(false);
    }
  };

  if (allServerLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor: "#000",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const menuItems = [
    // {
    //   label: "Usage",
    //   icon: <Usage fontSize="small" />,
    //   // action: () => navigate("/dashboard/usage"),
    //   // path: "/dashboard/usage",
    //   rightElement: (
    //     <Chip
    //       label="0/300"
    //       size="small"
    //       sx={{
    //         backgroundColor: "rgba(255, 255, 255, 0.1)",
    //         color: "white",
    //         fontSize: "0.75rem",
    //       }}
    //     />
    //   ),
    // },
    {
      label: "All Apps",
      icon: <AppsIcon fontSize="small" />,
      action: () => navigate("/dashboard/apps"),
      path: "/dashboard/apps",
    },
    {
      label: "Support",
      icon: <Support fontSize="small" />,
      action: () => navigate("/dashboard/support"),
      path: "/dashboard/support",
    },
    {
      label: "Accounts",
      icon: <Account fontSize="small" />,
      action: () => navigate("/dashboard/account"),
      path: "/dashboard/account",
    },
    // {
    //   label: "Logout",
    //   icon: <Logout fontSize="small" />,
    //   action: handleLogout,
    //   divider: true,
    // },
  ];

  // Sidebar content - extracted to be reused in both permanent sidebar and drawer
  const sidebarContent = (
    <>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 4 }}>
          <img
            src={Logo}
            style={{ width: "180px", height: "29px" }}
            alt="mcp-logo"
          />
        </Stack>

        <ButtonCommon
          endIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
          sx={{ mb: 2 }}
        >
          New MCP Server
        </ButtonCommon>

        <Box sx={{ overflowY: "auto", maxHeight: "calc(85vh - 200px)" }}>
          {servers.length > 0 && (
            <>
              {/* Servers list */}
              {servers.map((server) => {
                const isSelected =
                  selectedServer?.mcp_server_id === server.mcp_server_id;

                return (
                  <Box
                    key={server.mcp_server_id}
                    sx={{
                      mb: 2,
                      bgcolor: isSelected ? "#F7FFD454" : "none",
                      borderRadius: 2,
                      py: 1,
                      px: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      // "&:hover": { bgcolor: "#333333" },
                      cursor: "pointer",
                      border: isSelected ? "none" : "1px solid #F7FFD417",
                    }}
                    onClick={() => handleServerClick(server)}
                  >
                    <Typography color={"white"}>
                      {server.server_name}
                    </Typography>
                    <IconButton
                      edge="end"
                      onClick={async (e) => {
                        e.stopPropagation();

                        setOpenDeleteModal(true);
                        setSelectedServerToDelete(server.mcp_server_id);
                        // setIsServerActive(isSelected);
                        // await handleDeleteServer(server.mcp_server_id);

                        // if (isSelected) {
                        //   setSelectedServer(null);
                        //   navigate("/dashboard");
                        // }
                      }}
                      sx={{ color: "#888", width: "35px" }}
                    >
                      <Box
                        component={Delete}
                        sx={{
                          width: 20,
                          height: 20,
                        }}
                      />
                    </IconButton>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ width: "100%" }}>
        <Divider sx={{ mb: 1, borderColor: "rgba(255, 255, 255, 0.12)" }} />
        <List sx={{ py: 0 }}>
          {menuItems.map((item) => {
            const isActive =
              item.path && window.location.pathname === item.path;
            return (
              <React.Fragment key={item.label}>
                {item.divider && (
                  <Divider
                    sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.12)" }}
                  />
                )}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={item.action}
                    sx={{
                      color: "white",
                      backgroundColor: isActive
                        ? "rgba(255, 255, 255, 0.15)"
                        : "transparent",
                      borderRadius: 2,
                      mx: 1,
                      mb: 0.5,
                      border: isActive
                        ? "1px solid rgba(255, 255, 255, 0.2)"
                        : "1px solid transparent",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: isActive
                          ? "rgba(255, 255, 255, 0.2)"
                          : "rgba(255, 255, 255, 0.08)",
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      // cursor: item.label === "Usage" ? "none" : "pointer",
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive ? "#fff" : "rgba(255, 255, 255, 0.8)",
                        minWidth: 36,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: isActive ? "#fff" : "rgba(255, 255, 255, 0.9)",
                        fontWeight: isActive ? 500 : 400,
                      }}
                    />
                    {item.rightElement && (
                      <Box sx={{ ml: "auto" }}>{item.rightElement}</Box>
                    )}
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            );
          })}

          {/* User profile with dropdown */}
          <Divider sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.12)" }} />
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleUserClick}
              sx={{
                color: "white",
                borderRadius: 2,
                mx: 1,
                mb: 0.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  borderColor: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ListItemIcon
                  sx={{ color: "rgba(255,255,255,0.8)", minWidth: 36 }}
                >
                  <AccountCircleOutlined fontSize="medium" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    user?.fullName ||
                    user.emailAddresses[0]?.emailAddress ||
                    "User"
                  }
                  primaryTypographyProps={{
                    variant: "body2",
                    color: "#fff",
                    fontWeight: 500,
                    sx: {
                      whiteSpace: "normal", // allow line breaks
                      wordBreak: "break-word", // break long words if needed
                      overflowWrap: "break-word", // ensures it breaks within the sidebar
                    },
                  }}
                />
              </Box>
              <KeyboardArrowDown sx={{ color: "#fff" }} />
            </ListItemButton>
          </ListItem>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                mt: 0,
                ml: 1, // Add some margin to the left for spacing
                minWidth: 160,
                borderRadius: 2,
                backgroundColor: "#495125",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                color: "#fff",
                maxHeight: 200,
                overflow: "auto",
              },
            }}
          >
            <MenuItem
              onClick={handleUpgrade}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "white",
              }}
            >
              <WorkspacePremiumIcon fontSize="small" />
              Upgrade to Pro
            </MenuItem>
            <Divider sx={{ backgroundColor: "rgba(255,255,255,0.2)" }} />
            <MenuItem
              onClick={handleLogout}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "white",
              }}
            >
              <Logout fontSize="small" />
              Logout
            </MenuItem>
          </Menu>
        </List>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: "flex", height: "100vh" }} ref={divRef}>
      {/* Sidebar for desktop - hidden on mobile */}
      {!isMobile && (
        <Paper
          elevation={0}
          sx={{
            width: sidebarWidth,
            minWidth: sidebarWidth,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            bgcolor: "#1E1E1E",
            color: "#EDEDED",
            p: 2,
            borderRadius: 0,
          }}
        >
          {sidebarContent}
        </Paper>
      )}

      {/* Drawer for mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: sidebarWidth,
            bgcolor: "#1E1E1E",
            color: "#EDEDED",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          {sidebarContent}
        </Box>
      </Drawer>

      {/* Main Content Area with App Bar */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#000",
        }}
      >
        {/* App Bar */}
        <AppBar position="static" sx={{ bgcolor: "#000", pt: 2 }}>
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {isMobile && (
                <IconButton
                  color="primary"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Typography
                variant="h6"
                sx={{ color: "#fff", fontWeight: "400" }}
              >
                {selectedServer
                  ? selectedServer.server_name
                  : `Hello, ${
                      user?.fullName ||
                      user.emailAddresses[0]?.emailAddress ||
                      "User"
                    }`}
              </Typography>
            </Box>
            {/* <Box display="flex" alignItems="center">
              <Avatar
                src={user?.imageUrl}
                alt={
                  user?.fullName ||
                  user.emailAddresses[0]?.emailAddress ||
                  "User"
                }
                sx={{ width: 32, height: 32, mr: 1 }}
              />
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 500,
                  mr: 1,
                  display: { xs: "none", sm: "block" },
                }}
              >
                {user?.fullName ||
                  user.emailAddresses[0]?.emailAddress ||
                  "User"}
              </Typography>
            </Box> */}
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box sx={{ flex: 1, px: { xs: 0, md: 3 } }}>
          <RightSideContainer.Provider value={{ divHeight }}>
            {servers.length === 0 && isMainRoute ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "center",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 1,
                  height: "100%",
                }}
              >
                <img
                  src={Logo}
                  style={{ width: "80px", marginBottom: "10px" }}
                  alt="mcp-logo"
                />
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ mb: 1, color: "#F4F4F4" }}
                >
                  No MCP Server
                </Typography>
                <Typography
                  variant="body1"
                  color="white"
                  sx={{ mb: 3, maxWidth: 450, mx: "auto", color: "#AEAEAE" }}
                >
                  Create Your First MCP Server to Start Connecting your AI
                  Assistants to thousands of apps on MCP.Cloud
                </Typography>
                <ButtonCommon
                  endIcon={<AddIcon />}
                  onClick={() => setOpenModal(true)}
                  sx={{ width: "max-content" }}
                >
                  New MCP Server
                </ButtonCommon>
              </Box>
            ) : (
              <Outlet />
            )}
          </RightSideContainer.Provider>
        </Box>
      </Box>

      {/* New Server Modal */}
      {/* <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={style} variant="outlined">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" color="white" fontWeight="bold">
              New MCP Server
            </Typography>
            <IconButton
              onClick={() => setOpenModal(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ background: "#191919", borderRadius: "10px", p: 3 }}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (newServerName.trim()) {
                  addServer(newServerName.trim());
                  setNewServerName("");
                  setOpenModal(false);
                }
              }}
            >
              <InputLabel required sx={{ color: "white", mb: 1 }}>
                Name
              </InputLabel>
              <TextField
                fullWidth
                size="small"
                value={newServerName}
                onChange={(e) => setNewServerName(e.target.value)}
                required
                sx={{
                  bgcolor: "#909090",
                  borderRadius: 1,
                  "& .MuiInputLabel-root": { color: "#aaa" },
                }}
              />
              <Divider sx={{ my: 2 }} />
              <Box gap={2} display="flex">
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    textTransform: "capitalize",
                    fontWeight: "bold",
                    borderRadius: 4,
                  }}
                >
                  Create MCP Server
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Modal> */}

      {/* Create MCP Server Modal */}
      <CommonModal
        open={openModal}
        // setOpen={setOpenModal}
        handleClose={() => {
          setOpenModal(false);
          setNewServerName("");
        }}
        title="New MCP Server"
        cancelProps={{ sx: { display: "none" } }}
        submitProps={{ sx: { display: "none" } }}
      >
        <Box variant="outlined" sx={{ borderRadius: "10px" }}>
          <Box>
            <form>
              <InputLabel
                required
                sx={{
                  color: "#D6D6D6",
                  mb: 1,
                  "& .MuiInputLabel-asterisk": {
                    color: "red",
                  },
                }}
              >
                Name
              </InputLabel>
              <TextField
                fullWidth
                size="small"
                value={newServerName}
                onChange={(e) => setNewServerName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateServer();
                  }
                }}
                required
                sx={{
                  borderRadius: 1,
                  background: "#202020",
                  input: { color: "#fff" },
                  "& .MuiInputLabel-root": { color: "#aaa" },
                  mb: 2,
                  border: "1.39px solid #FFFFFF80",
                }}
              />
              <Box
                gap={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <ButtonCommon
                  variant="contained"
                  sx={{
                    borderRadius: 6,
                    width: "max-content",
                    boxShadow: "0px 4px 10.2px 3px #CFFF045C",
                  }}
                  size="small"
                  onClick={handleCreateServer}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Create MCP Server"
                  )}
                </ButtonCommon>
              </Box>
            </form>
          </Box>
        </Box>
      </CommonModal>

      {/* Delete Confirm Modal */}
      <CommonModal
        open={openDeleteModal}
        // setOpen={setOpenDeleteModal}
        handleClose={() => {
          setOpenDeleteModal(false);
        }}
        title="Delete MCP Server"
        cancelText="Cancel"
        cancelProps={{
          variant: "outlined",
          onClick: () => {
            setOpenDeleteModal(false);
          },
          sx: { color: "#fff", borderColor: "#fff" },
        }}
        submitText={deleteLoader ? "Deleting..." : "Delete"}
        submitProps={{
          disabled: deleteLoader,
          variant: "contained",
          sx: {
            ...(deleteLoader && {
              backgroundColor: "#999", // custom background
              color: "#fff", // custom text color
              "&.Mui-disabled": {
                backgroundColor: "#999",
                color: "#fff",
              },
            }),
          },
          onClick: () => handleDeleteServer(),
        }}
      >
        <Box>
          <Typography variant="body1" color="white" sx={{ mb: 2 }}>
            Are you sure you want to delete this MCP Server? This action cannot
            be undone.
          </Typography>
        </Box>
      </CommonModal>
    </Box>
  );
};

export default DashboardLayout;
